/**
 * Справочники, общие для фронтенда и бэкенда: размеры футболки, пол спортсмена и
 * виды программы.
 *
 * Дропдауны формы (src/frontend/application.ts) и валидация заявки
 * (src/backend/application/applicationValidation.ts) берут значения отсюда,
 * поэтому список не может разойтись между интерфейсом и API.
 *
 * Виды программы подключаются из `reference-data/disciplines.json` на сборке:
 * файл остаётся машиночитаемым источником для организаторов (правки — только им),
 * а интерфейс и функции получают тот же список без обращения к хранилищу в
 * рантайме. Чтение справочников из конфигурационного бакета — будущая задача.
 */
import disciplinesData from '../../reference-data/disciplines.json';
import documentsData from '../../reference-data/athlete-documents.json';
import stationsData from '../../reference-data/stations.json';
import type { DisciplineId, DocumentId, Gender, StationId, TshirtSize } from './types';

/**
 * Размеры футболки (задача 3: справочник в коде; из файла — в будущих версиях).
 * Порядок — от меньшего к большему, поэтому `XS` стоит первым: так размеры идут и в
 * дропдаунах формы. Список один и для спортсменов, и для представителей.
 */
export const TSHIRT_SIZES: TshirtSize[] = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

/** Размеры футболки одной строкой — для текста ошибки валидации (список не дублируется в текстах). */
export const TSHIRT_SIZES_TEXT = TSHIRT_SIZES.join(', ');

/** Пол спортсмена: код, который хранится в заявке, и подпись для интерфейса. */
export const GENDERS: { value: Gender; label: string }[] = [
  { value: 'M', label: 'М' },
  { value: 'F', label: 'Ж' },
];

/** Подпись пола по коду: просмотр строки спортсмена и сообщения об ошибках. */
export const GENDER_LABELS: Record<Gender, string> = { M: 'М', F: 'Ж' };

/** Вид программы: код из справочника и название для интерфейса. */
export interface Discipline {
  id: DisciplineId;
  name: string;
}

/** Виды программы — дропдаун диалога «Виды программы» в строке спортсмена. */
export const DISCIPLINES: Discipline[] = disciplinesData.disciplines;

/** Коды видов программы: по ним валидация отличает значение справочника от опечатки. */
export const DISCIPLINE_IDS: ReadonlySet<string> = new Set(
  DISCIPLINES.map((discipline) => discipline.id),
);

/** Начало названия видов программы «Дуйлянь»: по нему секция отбирает свои виды из справочника. */
const DUILIAN_NAME_PREFIX = 'Дуйлянь';

/**
 * Виды программы «Дуйлянь» — дропдаун «Тип» секции «Дуйлянь» и его валидация.
 * Это подмножество общего справочника, отобранное по началу названия: организатор
 * правит только `disciplines.json`, а состав секции подстраивается под справочник
 * (`Дуйлянь` без оружия / с оружием / прочие — при их наличии).
 */
export const DUILIAN_DISCIPLINES: Discipline[] = DISCIPLINES.filter((discipline) =>
  discipline.name.startsWith(DUILIAN_NAME_PREFIX),
);

/** Коды видов программы «Дуйлянь»: по ним валидация отличает значение справочника от опечатки. */
export const DUILIAN_DISCIPLINE_IDS: ReadonlySet<string> = new Set(
  DUILIAN_DISCIPLINES.map((discipline) => discipline.id),
);

/**
 * Признак «спортсмену добавлен хотя бы один вид программы «Дуйлянь»»: по нему
 * дропдауны спортсменов секции «Дуйлянь» отбирают, кого можно выбрать (правка
 * организатора 18.10.2026: в выступлении участвуют только заявленные на «Дуйлянь»).
 * Пустой список видов программы — тоже «нет»: спортсмен в секцию не попадает.
 * Тип аргумента структурный: функция не тянет за собой модель заявки и годится
 * любому списку с полем `disciplines`.
 */
export function hasDuilianDiscipline(athlete: { disciplines: DisciplineId[] }): boolean {
  return athlete.disciplines.some((disciplineId) => DUILIAN_DISCIPLINE_IDS.has(disciplineId));
}

/** Название вида программы по коду — его сохраняет строка «Дуйляня» рядом с кодом. */
export function disciplineName(disciplineId: DisciplineId): string {
  return DISCIPLINES.find((discipline) => discipline.id === disciplineId)?.name ?? '';
}

/** Тип документа спортсмена: код из справочника и название для диалога «Документы». */
export interface DocumentType {
  id: DocumentId;
  title: string;
}

/**
 * Типы документов — строки диалога «Документы» (задача 11). Показываются все типы
 * словаря: правила, скрывающие часть типов по полям спортсмена, — будущие версии
 * (reference-data/athlete-documents.md). В заявке названия не хранятся: строка
 * диалога собирается из словаря по `docId`.
 */
export const DOCUMENTS: DocumentType[] = documentsData.documents;

/** Коды типов документов: по ним API отличает значение справочника от опечатки. */
export const DOCUMENT_IDS: ReadonlySet<string> = new Set(
  DOCUMENTS.map((document) => document.id),
);

/** Название типа документа по коду — для писем и служебных сообщений. */
export function documentTitle(docId: DocumentId): string {
  return DOCUMENTS.find((document) => document.id === docId)?.title ?? docId;
}

/** Станция приезда/отъезда: код из справочника и название для интерфейса. */
export interface Station {
  id: StationId;
  name: string;
}

/**
 * Станции приезда/отъезда — дропдаун «Вокзал или аэропорт» подсекций «Приезд»/«Отъезд».
 * Как и виды программы, список подключается из `reference-data/stations.json` на сборке:
 * файл остаётся источником для организаторов, а интерфейс и валидация получают один
 * и тот же список без обращения к хранилищу в рантайме.
 */
export const STATIONS: Station[] = stationsData.stations;

/** Коды станций: по ним валидация отличает значение справочника от опечатки. */
export const STATION_IDS: ReadonlySet<string> = new Set(STATIONS.map((station) => station.id));

/**
 * Код станции «Самостоятельно» — последний пункт дропдауна «Вокзал / аэропорт» (правка
 * организатора 19.10.2026): команда добирается своим ходом, трансфер организаторов ей не
 * нужен. Пункт живёт в справочнике `reference-data/stations.json` вместе с вокзалами,
 * поэтому код проходит валидацию и показывается словами в просмотре строки и на экране
 * организатора.
 */
export const SELF_STATION_ID: StationId = 'self';

/**
 * Возможен ли трансфер организаторов для строки приезда/отъезда: нужен выбранный вокзал
 * или аэропорт, а «Самостоятельно» и пустой вариант означают, что встречать команду негде.
 * По этому признаку форма выключает чекбокс «Трансфер нужен», а бэкенд принудительно снимает
 * флаг у такой строки (`normalizeTransfer`). Функция общая для фронтенда и бэкенда — правило
 * одно, чтобы интерфейс и API не разошлись.
 */
export function canOrderTransfer(stationId: StationId): boolean {
  return stationId !== '' && stationId !== SELF_STATION_ID;
}

/** Название станции по коду — для писем и служебных сообщений. */
export function stationName(stationId: StationId): string {
  return STATIONS.find((station) => station.id === stationId)?.name ?? stationId;
}
